import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

import { AHSwitch, Box, ListItem } from "arkh";
import { RoutineEditScreenProps } from "./RoutineEditNavigation";
import {
  useRoutineEditState,
  useRoutineEditStateSetParamsFunction,
} from "../State/RoutineEditState";
import { useMemo } from "react";
import { RoutineEditOrgTopicListItem } from "./RoutineEditMainOrgTopicListItem";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WorkspaceRoutineEditSectionSettingsQuery } from "./__generated__/WorkspaceRoutineEditSectionSettingsQuery.graphql";
import { FormattedMessage } from "react-intl";

export function WorkspaceRoutineEditSectionSettings({
  navigation,
  route,
}: RoutineEditScreenProps<"WorkspaceRoutineEditSectionSettings">) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWorkspaceRoutineEditSectionSettings
          sectionId={route.params.sectionId}
          orgWorkspaceId={route.params.orgWorkspaceId}
          onDone={() => {
            navigation.goBack();
          }}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWorkspaceRoutineEditSectionSettings(props: {
  sectionId: string;
  orgWorkspaceId: string;
  onDone: () => void;
  fetchKey: string;
}) {
  const { sections } = useRoutineEditState();
  const data = useLazyLoadQuery<WorkspaceRoutineEditSectionSettingsQuery>(
    graphql`
      query WorkspaceRoutineEditSectionSettingsQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          topics {
            id
            name
            isPrivate
            ...RoutineEditMainOrgTopicListItem_topics
          }
        }
      }
    `,
    {
      workspaceId: props.orgWorkspaceId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const setParams = useRoutineEditStateSetParamsFunction();
  const section = useMemo(
    () => sections?.find((section) => section.id === props.sectionId),
    [props.sectionId, sections]
  );

  useNavigationHeader({
    type: "DONE",
    onDone: props.onDone,
    defaultContentStyle: true,
    title: section?.title ?? "",
  });

  const topics = data.workspace?.topics ?? [];
  return (
    <Box space="large">
      <Box spaceBottom="large">
        <ListItem
          isFree
          disabled
          border="border-primary"
          right={
            <AHSwitch
              value={section?.orgCanBeDisabled ?? false}
              onValueChange={(value) => {
                setParams((prev) => ({
                  ...prev,
                  sections: prev.sections?.map((section) =>
                    section.id === props.sectionId
                      ? { ...section, orgCanBeDisabled: value }
                      : section
                  ),
                }));
              }}
            />
          }
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Allow units to hide locally"
              description="WorkspaceRoutineEditSectionSettings allow units to disable"
            />
          </ListItem.Title>
        </ListItem>
      </Box>
      <RoutineEditOrgTopicListItem
        topics={topics}
        selectedTopic={section?.orgTopic?.id ?? "NONE"}
        onSelectedTopicChange={(topic) => {
          const topicData = topics.find((t) => t.id === topic) ?? null;

          setParams((prev) => ({
            ...prev,
            sections: prev.sections?.map((section) =>
              section.id === props.sectionId
                ? { ...section, orgTopic: topicData }
                : section
            ),
          }));
        }}
      >
        <ListItem.SubTitle>
          <FormattedMessage
            defaultMessage="This section will only be visible for units who are assigned to this topic. If the topic is subscribed by one or multiple teams locally, then issues created in this section will automatically be shared with assigned teams."
            description="WorkspaceRoutineEditSectionSettings topic sub title"
          />
        </ListItem.SubTitle>
      </RoutineEditOrgTopicListItem>
    </Box>
  );
}
